/**
 * Returns the average of two numbers.
 *
 * @remarks
 * This method is part of the {@link core-library#Statistics | Statistics subsystem}.
 *
 * @param x - The first input number
 * @param y - The second input number
 * @returns The arithmetic mean of `x` and `y`
 *
 */
import { Context } from '../types';

import Api from './Api';

function getAverage(x: number, y: number): number {
    return (x + y) / 2.0;
}

const getContextDefaultValues = <T extends {}>(api: Api<T>): Context<T> => {
  return {
    selected: null,
    setSelected: () => null,
    data: [],
    get: api.get,
    getById: api.getById,
    post: api.post,
    put: api.put,
    remove: api.remove,
    setData: () => {},
  };
};

const duplicateMock = <T extends {}>(mock: T[], fieldToGetIndex: keyof T, amount: number): T[] => {
  return mock.reduce((acc: T[], next) => {
    return acc.concat(
      Array(amount)
        .fill(0)
        .map((_, index) => ({
          ...next,
          [fieldToGetIndex]: `${next[fieldToGetIndex]}${index + 1}`,
        }))
    );
  }, []);
};

export type ProviderDataStructure<T> = {
  allIds: string[];
  byId: Record<string, T>;
};

function getArrayFromProviderData<T>(data: ProviderDataStructure<T>): T[] {
  return data.allIds.map((id) => data.byId[id]);
}

// --- Exports ---
export {
  getAverage,
  getContextDefaultValues,
  duplicateMock,
  getArrayFromProviderData,
};
